// src/lib/apercu-isole.js — faille N1 (revue du 29/10, lot L5).
//
// Ouvre un document HTML (facture, reçu, avoir, copie archivée, rapport) dans
// un nouvel onglet SANS jamais l'écrire dans une page de l'appli.
//
// Avant : `window.open('', '_blank')` puis `document.write(html)`. L'onglet
// vierge a la MÊME origine que l'appli : un nom de voyageur piégé
// (`<img src=x onerror=…>`) s'y exécutait avec la session de l'admin.
//
// Maintenant : l'onglet reçoit une coque FIXE (aucune donnée dedans, hors
// l'attribut srcdoc) qui contient une <iframe srcdoc> SANDBOXÉE — même modèle
// que la page publique du reçu (src/RecuPage.jsx). Le document lui-même n'est
// jamais modifié : une pièce émise reste sa copie figée.
//
// Deux bacs à sable :
//   · documents (facture, reçu, avoir) : `allow-same-origin allow-modals`,
//     SANS `allow-scripts` — aucun script du document ne tourne, aucun
//     formulaire, aucune fenêtre surgissante, aucune navigation de l'onglet.
//     `allow-same-origin` sert seulement à la coque : recopier le titre et
//     brancher le bouton « Imprimer » du document (son onclick est inerte
//     dans le bac à sable) sur l'impression du cadre. `allow-modals` est
//     requis par print(). Rendu à l'écran identique : même bouton, même place.
//   · rapport mensuel (graphiques Chart.js) : `allow-scripts allow-modals`,
//     SANS `allow-same-origin` — ses scripts tournent dans une origine
//     opaque : ni session, ni stockage, ni accès à l'appli ou à l'onglet
//     d'origine. Ne jamais réunir les deux jetons (le cadre pourrait alors
//     retirer son propre bac à sable).

// Attribut HTML : `&` et `"` suffisent dans une valeur entre guillemets ;
// `<` et `>` en plus, par prudence.
function echapperAttribut(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

export const SANDBOX_DOCUMENT = 'allow-same-origin allow-modals'
export const SANDBOX_AVEC_SCRIPTS = 'allow-scripts allow-modals'

// Hauteur à poser sur le cadre pour l'impression, ou null pour ne rien
// changer. Jamais PENDANT l'impression : un document dont la hauteur suit la
// fenêtre (body{min-height:100vh}) grandirait avec le cadre, sans fin. Plafond
// de 200 000 px (une hauteur reçue du rapport n'est qu'un message).
export const HAUTEUR_MAX_PX = 200000
export function hauteurPermise(h, enImpression) {
  if (enImpression) return null
  if (typeof h !== 'number' || !isFinite(h) || h <= 0) return null
  return Math.min(Math.ceil(h), HAUTEUR_MAX_PX)
}

// Script FIXE de la coque (aucune donnée dedans).
//   · Document sans scripts : au chargement, titre recopié (texte seul) ; si
//     le document a son bouton « Imprimer », il est branché sur l'impression
//     du cadre et celui de la coque reste caché ; sinon (copie archivée) le
//     bouton de la coque s'affiche.
//   · Impression par le menu ou Cmd+P : la coque imprime son cadre, coupé à la
//     hauteur de la fenêtre. La hauteur réelle du document est donc posée sur
//     le cadre (variable --hauteur-doc, lue par la règle @media print), au
//     chargement puis juste avant l'impression. Le rapport (origine opaque)
//     envoie lui-même sa hauteur par message. Le chargement du cadre peut
//     précéder ce script (WebKit) : il est aussi traité tout de suite.
const SCRIPT_COQUE = `(function(){
// Même règle que hauteurPermise ci-dessus (testée).
var cadre=document.getElementById('doc'),bouton=document.getElementById('imprimer');
function hauteurPermise(h,enImpression){if(enImpression)return null;if(typeof h!=='number'||!isFinite(h)||h<=0)return null;return Math.min(Math.ceil(h),${HAUTEUR_MAX_PX})}
function enImpression(){return !!(window.matchMedia&&window.matchMedia('print').matches)}
function poserHauteur(h){var v=hauteurPermise(h,enImpression());if(v!==null)cadre.style.setProperty('--hauteur-doc',v+'px')}
function mesurer(){try{var d=cadre.contentDocument;if(d&&d.documentElement)poserHauteur(Math.max(d.documentElement.scrollHeight,d.body?d.body.scrollHeight:0))}catch(e){/* origine opaque (rapport) : la hauteur arrive par message */}}
function imprimer(){var w=cadre.contentWindow;w.focus();w.print()}
bouton.addEventListener('click',imprimer);
window.addEventListener('message',function(e){if(e.source===cadre.contentWindow&&e.data&&typeof e.data.fbzHauteurDocument==='number')poserHauteur(e.data.fbzHauteurDocument)});
window.addEventListener('beforeprint',mesurer);
var fait=false;
function auChargement(){
  if(fait||cadre.getAttribute('data-scripts')==='1')return;
  var d=cadre.contentDocument;
  if(!d||d.URL!=='about:srcdoc'||d.readyState!=='complete')return;
  fait=true;
  document.title=d.title||document.title;
  var b=d.querySelector('.print-btn');
  if(b)b.addEventListener('click',imprimer);else bouton.style.display='block';
  mesurer();
  // La hauteur bouge encore après (polices, images) : elle est suivie.
  if(window.ResizeObserver)new ResizeObserver(mesurer).observe(d.documentElement);
  if(d.fonts&&d.fonts.ready)d.fonts.ready.then(mesurer);
}
cadre.addEventListener('load',function(){auChargement();mesurer()});
auChargement();
})();`

/**
 * @param {string} html
 * @param {{ scripts?: boolean, titre?: string }} [options]
 *   scripts : le document a besoin de ses scripts (rapport) ; origine opaque.
 *             Il garde son propre bouton « Imprimer » (la coque ne peut pas
 *             imprimer un cadre d'une autre origine).
 *   titre   : titre de l'onglet quand la coque ne peut pas lire celui du
 *             document (origine opaque).
 */
export function coqueApercuIsole(html, options = {}) {
  const { scripts = false, titre = 'Aperçu' } = options
  const sandbox = scripts ? SANDBOX_AVEC_SCRIPTS : SANDBOX_DOCUMENT
  return `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="utf-8"/>
<title>${echapperAttribut(titre)}</title>
<link href="https://fonts.googleapis.com/css2?family=Raleway:wght@700&display=swap" rel="stylesheet">
<style>
  html,body{margin:0;padding:0;height:100%;overflow:hidden}
  #doc{display:block;border:0;width:100%;height:100%}
  #imprimer{display:none;position:fixed;top:20px;right:20px;background:#215387;color:#fff;border:none;border-radius:8px;padding:10px 18px;font-size:13px;font-weight:700;cursor:pointer;font-family:'Raleway',sans-serif;box-shadow:0 4px 12px rgba(33,83,135,.3);z-index:9999}
  @media print{
    html,body{height:auto;overflow:visible}
    #doc{height:var(--hauteur-doc,100%)}
    #imprimer{display:none!important}
  }
</style>
</head>
<body>
<button id="imprimer" type="button">🖨️ Imprimer / Enregistrer en PDF</button>
<iframe id="doc" title="Document" sandbox="${sandbox}"${scripts ? ' data-scripts="1"' : ''} referrerpolicy="no-referrer" srcdoc="${echapperAttribut(html)}"></iframe>
<script>${SCRIPT_COQUE}</script>
</body>
</html>`
}

/**
 * Ouvre `html` dans un nouvel onglet, isolé. Rend la fenêtre, ou null si le
 * navigateur a bloqué l'ouverture (l'appelant affiche son message habituel).
 * @param {string} html
 * @param {{ openWindow?: () => Window|null, scripts?: boolean, titre?: string }} [options]
 */
export function ouvrirApercuIsole(html, options = {}) {
  const { openWindow = () => window.open('', '_blank'), ...reste } = options
  const win = openWindow()
  if (!win) return null
  win.document.write(coqueApercuIsole(html == null ? '' : html, reste))
  win.document.close()
  return win
}
